import { useState } from 'react';
import axios from 'axios';
import { Loader2, Send, X } from 'lucide-react';
import { toast } from 'sonner';
import { devError } from '../utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TEMPLATES = [
  {
    label: 'Yes, available',
    body:
      "Hi,\n\nThanks for the enquiry — yes, still available. Happy to:\n• Put aside for 48h\n• Send extra photos\n• Arrange yard collection\n\nLet me know which works.\n\nCheers",
  },
  {
    label: 'Sold, similar in stock',
    body:
      "Hi,\n\nThanks for getting in touch — that one's just sold, but I have a few similar pieces in stock. Want me to send photos?\n\nCheers",
  },
  {
    label: 'More info needed',
    body:
      "Hi,\n\nThanks for the enquiry. Could you let me know:\n• Quantity needed\n• Project location (for delivery quote)\n• Timeline\n\nHappy to send more photos in the meantime.\n\nCheers",
  },
];

/**
 * Branded in-app reply modal — sends a real email to the buyer through PatinaMart
 * and marks the inquiry replied (auto-clears 24h/40h escalation).
 *
 * Used from both the desktop PendingEnquiriesPanel and the mobile Wingman inbox.
 */
export default function ReplyDialog({ open, onClose, inquiry, onReplied, mobile }) {
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);

  if (!open || !inquiry) return null;

  const close = () => {
    if (busy) return;
    setBody('');
    onClose?.();
  };

  const submit = async () => {
    const trimmed = body.trim();
    if (!trimmed) {
      toast.error('Type a message first');
      return;
    }
    if (!inquiry.inquiry_id) {
      toast.error("This older enquiry can't be replied to in-app — use email instead.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/supplier/inquiries/${inquiry.inquiry_id}/reply`,
        { body: trimmed }
      );
      toast.success('Reply sent ✓');
      onReplied?.(data);
      setBody('');
      onClose?.();
    } catch (e) {
      devError('reply failed', e);
      toast.error(e.response?.data?.detail || "Couldn't send reply");
    } finally {
      setBusy(false);
    }
  };

  const wrapperCls = mobile
    ? 'fixed inset-0 z-50 bg-[#0F172A] flex flex-col'
    : 'fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4';
  const panelCls = mobile
    ? 'flex-1 flex flex-col'
    : 'w-full max-w-lg rounded-2xl bg-card border border-border/30 shadow-2xl flex flex-col max-h-[85vh]';

  return (
    <div className={wrapperCls} role="dialog" aria-modal data-testid="reply-dialog">
      <div className={panelCls}>
        <div className={`flex items-start justify-between ${mobile ? 'px-5 py-4 border-b border-white/10' : 'p-5 border-b border-border/30'}`}>
          <div className="min-w-0">
            <p className={`${mobile ? 'text-[11px] text-[#D4AF37] uppercase tracking-[0.2em]' : 'text-xs uppercase tracking-widest text-accent'}`}>Reply via PatinaMart</p>
            <h3 className={`${mobile ? 'text-white' : 'text-foreground'} font-semibold truncate`}>{inquiry.listing_title || 'Reply'}</h3>
            <p className={`${mobile ? 'text-slate-400' : 'text-muted-foreground'} text-xs mt-0.5 truncate`}>
              To: {inquiry.buyer_name || 'Buyer'}{inquiry.user_email ? ` <${inquiry.user_email}>` : ''}
            </p>
          </div>
          <button onClick={close} disabled={busy} className={`${mobile ? 'text-slate-300' : 'text-muted-foreground'} hover:opacity-80 ml-2`} data-testid="reply-dialog-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className={`${mobile ? 'flex-1 overflow-y-auto' : ''} px-5 py-4 space-y-3`}>
          {inquiry.message && (
            <details className={`${mobile ? 'text-slate-300' : 'text-muted-foreground'} text-xs`}>
              <summary className="cursor-pointer hover:opacity-80">Original message</summary>
              <p className="mt-2 whitespace-pre-wrap text-[13px] leading-snug">{inquiry.message}</p>
            </details>
          )}

          <div className="flex flex-wrap gap-2">
            {TEMPLATES.map((t) => (
              <button
                key={t.label}
                onClick={() => setBody(t.body)}
                disabled={busy}
                className={`${mobile ? 'text-[11px] bg-white/5 border-white/10 text-slate-200' : 'text-xs bg-accent/5 border-accent/20 text-accent'} px-2.5 py-1 rounded-full border hover:opacity-80`}
                data-testid={`reply-dialog-template-${t.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Type your reply…"
            rows={mobile ? 10 : 8}
            disabled={busy}
            className={`w-full px-4 py-3 rounded-xl ${mobile ? 'bg-white/5 border-white/10 text-white' : 'bg-background border-border/30 text-foreground'} border text-sm leading-relaxed focus:outline-none focus:border-[#D4AF37] resize-none`}
            data-testid="reply-dialog-textarea"
          />
          <p className={`text-[11px] ${mobile ? 'text-slate-500' : 'text-muted-foreground'}`}>
            Sent through PatinaMart. The buyer can hit reply to come back to you directly.
          </p>
        </div>

        <div className={`flex items-center gap-2 ${mobile ? 'px-5 py-4 border-t border-white/10' : 'p-4 border-t border-border/30'}`}>
          <button
            onClick={close}
            disabled={busy}
            className={`flex-1 px-4 py-2.5 rounded-full ${mobile ? 'bg-white/5 border-white/10 text-slate-200' : 'bg-muted/30 text-muted-foreground'} text-sm font-medium border disabled:opacity-50`}
            data-testid="reply-dialog-cancel"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={busy || !body.trim()}
            className="flex-1 px-4 py-2.5 rounded-full bg-[#D4AF37] text-black text-sm font-bold disabled:opacity-50 flex items-center justify-center gap-1.5"
            data-testid="reply-dialog-send"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Send reply
          </button>
        </div>
      </div>
    </div>
  );
}
